import { Badge, Box, Table } from "@chakra-ui/react";
import { Tooltip } from "./ui/tooltip";
import {
  CircleCheck,
  CircleX,
  ClipboardList,
  Clock,
  Minus,
  RefreshCw,
  Volume2,
  VolumeOff,
} from "lucide-react";
import { formatDatetime, formatUtcDatetimeToColombian } from "../helpers/time";
import useNumberFormatter from "../hooks/useNumberFormatter";

type Props = {
  value: string | number | boolean | undefined;
  formatDatetimeUtc?: boolean;
  formatDatetimeColombian?: boolean;
  booleanIcon?: boolean;
  statusIcon?: boolean;
  badge?: boolean;
  actions?: React.ReactNode;
  formatNumber?: boolean;
  onClick?: () => void;
  tooltip?: string;
  suffix?: React.ReactNode;
};

type BadgeValue = keyof typeof mapValueToBadge;

const getBooleanIcon = (value: boolean) =>
  value ? (
    <CircleCheck color="#3CB18B" size={20} strokeWidth={2} />
  ) : (
    <CircleX color="#EC5858" size={20} strokeWidth={2} />
  );

const mapStatusToIcon = {
  success: <CircleCheck color="#3CB18B" size={20} strokeWidth={2} />,
  error: <CircleX color="#EC5858" size={20} strokeWidth={2} />,
  none: <Minus color="#9F9DB0" size={20} strokeWidth={2} />,
  off_hours: <Clock color="#E6AE22" size={20} strokeWidth={2} />,
  processing: <RefreshCw color="#E6AE22" size={20} strokeWidth={2} />,
  retry: <RefreshCw color="#3CB18B" size={20} strokeWidth={2} />,
  manual: <ClipboardList color="#3CB18B" size={20} strokeWidth={2} />,
  pending: <Minus color="#9F9DB0" size={20} strokeWidth={2} />,
  sent: <CircleCheck color="#3CB18B" size={20} strokeWidth={2} />,
  failed: <CircleX color="#EC5858" size={20} strokeWidth={2} />,
  muted: <VolumeOff color="#EC5858" size={20} strokeWidth={2} />,
  unmuted: <Volume2 color="#3CB18B" size={20} strokeWidth={2} />,
};

const mapValueToBadge = {
  card: { label: "Tarjeta", bg: "#DFEAFF", color: "#6887D8" },
  bancolombiaButton: {
    label: "Bancolombia",
    bg: "#FFF7D6",
    color: "#E6AE22",
  },
  nequiButton: { label: "Nequi", bg: "#EDEBFF", color: "#7B61FA" },
  pse: { label: "PSE", bg: "#FFE7D6", color: "#F0873A" },
  chargeback: { label: "Reversado", bg: "#F3E5F5", color: "#7B1FA2" },
  paid: { label: "Pagado", bg: "#E3FFE4", color: "#3CB18B" },
  partiallyPaid: { label: "Pago Parcial", bg: "#E4F6F8", color: "#0E7C86" },
  paymentProcessing: {
    label: "Pago en Curso",
    bg: "#D0E7FF",
    color: "#1E90FF",
  },
  ready: { label: "Sin Pagar", bg: "#FFF7D6", color: "#E6AE22" },
  cancelledPaymentEnabled: {
    label: "Pago Habilitado",
    bg: "#FFF3E0",
    color: "#F57C00",
  },
  pending: { label: "Sin Cliente", bg: "#DFEAFF", color: "#6887D8" },
  cancelled: { label: "Archivado", bg: "#FFDCDC", color: "#EC5858" },
  processing: { label: "Procesando", bg: "#FFF7D6", color: "#E6AE22" },
  credited: { label: "Acreditado", bg: "#D0E7FF", color: "#1E90FF" },
  error: { label: "Error", bg: "#FFDCDC", color: "#EC5858" },
  whatsapp: { label: "Whatsapp", bg: "#3CB18B", color: "#E3FFE4" },
  portal: { label: "Portal", bg: "#F7F7F7", color: "#9F9DB0" },
  manual: { label: "Manual", bg: "#b1caf0", color: "#1169f0" },
  bulk: { label: "Archivo", bg: "#94ebcb", color: "#048254" },
  erp: { label: "ERP", bg: "#a3dee6", color: "#029db3" },
  api: { label: "API", bg: "#d4c5f9", color: "#6b3fa0" },
  initial: { label: "Inicial", bg: "#94ebcb", color: "#048254" },
  due_soon: { label: "Recordatorio", bg: "#a3dee6", color: "#029db3" },
  expiration_date: { label: "Recuperación", bg: "#FFE7D6", color: "#F0873A" },
  postpaid: { label: "Mes Vencido", bg: "#CFF0FF", color: "#1E90FF" },
  prepaid: { label: "Descontada", bg: "#D0F8AA", color: "#048254" },
  1: { label: "Very High Risk", bg: "#FEE2E2", color: "#DC2626" },
  2: { label: "High Risk", bg: "#FFEDD5", color: "#EA580C" },
  3: { label: "Subprime", bg: "#FEF3C7", color: "#B45309" },
  4: { label: "Standard", bg: "#E0F2FE", color: "#0369A1" },
  5: { label: "Prime", bg: "#D1FAE5", color: "#047857" },
  "N/A": { label: "N/A", bg: "#F1F5F9", color: "#64748B" },
};

const wrapInBadge = (content: string, bg: string, color: string) => (
  <Badge color={color} bg={bg} size="md">
    {content}
  </Badge>
);

const GenericTableElement = ({
  value,
  formatDatetimeUtc,
  formatDatetimeColombian,
  booleanIcon,
  statusIcon,
  badge,
  actions,
  formatNumber,
  onClick,
  tooltip,
  suffix,
}: Props): JSX.Element => {
  const formatNumberValue = useNumberFormatter("es-CO", "COP", "number");

  let finalValue: React.ReactNode;

  if (formatDatetimeUtc && typeof value === "string") {
    finalValue = formatUtcDatetimeToColombian(value);
  } else if (formatDatetimeColombian && typeof value === "string") {
    finalValue = formatDatetime(value);
  } else if (booleanIcon && typeof value === "boolean") {
    finalValue = getBooleanIcon(value);
  } else if (statusIcon) {
    finalValue = mapStatusToIcon[value as keyof typeof mapStatusToIcon];
  } else if (badge) {
    if (typeof value === "string" && value in mapValueToBadge) {
      const badgeValue = value as BadgeValue;
      finalValue = wrapInBadge(
        mapValueToBadge[badgeValue]?.label || "",
        mapValueToBadge[badgeValue]?.bg || "",
        mapValueToBadge[badgeValue]?.color || ""
      );
    } else {
      finalValue = value;
    }
  } else if (actions) {
    finalValue = actions;
  } else if (formatNumber && typeof value === "number") {
    finalValue = formatNumberValue(value);
  } else {
    finalValue = value;
  }

  if (tooltip) {
    finalValue = (
      <Tooltip content={tooltip}>
        <Box as="span" display="inline-flex" cursor="help">
          {finalValue}
        </Box>
      </Tooltip>
    );
  }

  if (suffix) {
    finalValue = (
      <Box display="inline-flex" alignItems="center" gap={2}>
        {finalValue}
        {suffix}
      </Box>
    );
  }

  return (
    <Table.Cell
      cursor={onClick ? "pointer" : "default"}
      transition="color 0.2s ease, text-decoration 0.2s ease"
      wordBreak="break-word"
      overflowWrap="break-word"
      borderColor="#e5e7eb"
      whiteSpace="normal"
      overflow="hidden"
      onClick={onClick}
      _hover={onClick ? { textDecoration: "underline" } : undefined}
    >
      {finalValue}
    </Table.Cell>
  );
};

export default GenericTableElement;
